'use client';

import axios from 'axios';

const ALPHA_VANTAGE_API_KEY = process.env.NEXT_PUBLIC_ALPHA_VANTAGE_API_KEY || '';
const ALPHA_VANTAGE_BASE_URL = 'https://www.alphavantage.co/query';

// Cache for API responses
const cache = new Map<string, { data: any; timestamp: number }>();
const CACHE_DURATION = 60000; // 1 minute

export interface AlphaVantageQuote {
  symbol: string;
  price: number;
  change: number;
  changePercent: number;
  volume: number;
  high: number;
  low: number;
  open: number;
  previousClose: number;
}

export interface AlphaVantageGlobalQuote {
  '01. symbol': string;
  '02. open': string;
  '03. high': string;
  '04. low': string;
  '05. price': string;
  '06. volume': string;
  '07. latest trading day': string;
  '08. previous close': string;
  '09. change': string;
  '10. change percent': string;
}

export const alphaVantageService = {
  /**
   * Fetch real-time quote for a single symbol
   */
  async getQuote(symbol: string): Promise<AlphaVantageQuote | null> {
    const cacheKey = `quote_${symbol}`;
    const cached = cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    try {
      const response = await axios.get(`/api/alpha-vantage/quote`, {
        params: { symbol },
        timeout: 10000,
      });

      const quote = response.data as AlphaVantageQuote;
      
      if (!quote || !quote.price) {
        console.error('Invalid response from Alpha Vantage API:', response.data);
        return null;
      }

      cache.set(cacheKey, { data: quote, timestamp: Date.now() });
      return quote;
    } catch (error) {
      console.error(`Alpha Vantage API error for ${symbol}:`, error);
      return null;
    }
  },

  /**
   * Fetch quotes for multiple symbols
   */
  async getMultipleQuotes(symbols: string[]): Promise<Map<string, AlphaVantageQuote>> {
    const results = new Map<string, AlphaVantageQuote>();
    
    try {
      const response = await axios.post('/api/alpha-vantage/quotes', 
        { symbols },
        { 
          timeout: 30000, // Reduced to 30 seconds
          validateStatus: (status) => status < 500 // Don't throw on 4xx errors
        }
      );

      // Handle specific error responses
      if (response.status === 503) {
        console.warn('Alpha Vantage API key not configured');
        return results;
      }

      if (!response.data || !Array.isArray(response.data)) {
        console.warn('Invalid response format from Alpha Vantage API');
        return results;
      }

      const quotes = response.data as AlphaVantageQuote[];
      quotes.forEach((quote) => {
        if (quote && quote.price) {
          results.set(quote.symbol, quote);
        }
      });
    } catch (error: any) {
      // Handle 502 Bad Gateway specifically
      if (error.response?.status === 502) {
        console.error('Alpha Vantage service temporarily unavailable (502 Bad Gateway)');
      } else if (error.code === 'ECONNABORTED') {
        console.error('Alpha Vantage request timeout');
      } else {
        console.error('Alpha Vantage multiple quotes error:', error.message || error);
      }
    }

    return results;
  },

  /**
   * Fetch intraday data for charting
   */
  async getIntradayData(symbol: string, interval: '1min' | '5min' | '15min' | '30min' | '60min' = '5min') {
    if (!ALPHA_VANTAGE_API_KEY || ALPHA_VANTAGE_API_KEY === 'your-alpha-vantage-api-key-here') {
      console.warn('Alpha Vantage API key not configured');
      return null;
    }

    try {
      const response = await axios.get(ALPHA_VANTAGE_BASE_URL, {
        params: {
          function: 'TIME_SERIES_INTRADAY',
          symbol,
          interval,
          apikey: ALPHA_VANTAGE_API_KEY,
        },
        timeout: 10000,
      });

      return response.data;
    } catch (error) {
      console.error(`Alpha Vantage intraday data error for ${symbol}:`, error);
      return null;
    }
  },

  /**
   * Search for symbols
   */
  async searchSymbols(keywords: string) {
    if (!ALPHA_VANTAGE_API_KEY || ALPHA_VANTAGE_API_KEY === 'your-alpha-vantage-api-key-here') {
      console.warn('Alpha Vantage API key not configured');
      return null;
    }

    try {
      const response = await axios.get(ALPHA_VANTAGE_BASE_URL, {
        params: {
          function: 'SYMBOL_SEARCH',
          keywords,
          apikey: ALPHA_VANTAGE_API_KEY,
        },
        timeout: 10000,
      });

      return response.data.bestMatches || [];
    } catch (error) {
      console.error('Alpha Vantage symbol search error:', error);
      return null;
    }
  },

  /**
   * Get forex exchange rate
   */
  async getForexRate(fromCurrency: string, toCurrency: string) {
    if (!ALPHA_VANTAGE_API_KEY || ALPHA_VANTAGE_API_KEY === 'your-alpha-vantage-api-key-here') {
      console.warn('Alpha Vantage API key not configured');
      return null;
    }

    const cacheKey = `forex_${fromCurrency}_${toCurrency}`;
    const cached = cache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    try {
      const response = await axios.get(ALPHA_VANTAGE_BASE_URL, {
        params: {
          function: 'CURRENCY_EXCHANGE_RATE',
          from_currency: fromCurrency,
          to_currency: toCurrency,
          apikey: ALPHA_VANTAGE_API_KEY,
        },
        timeout: 10000,
      });

      const data = response.data['Realtime Currency Exchange Rate'];
      cache.set(cacheKey, { data, timestamp: Date.now() });
      return data;
    } catch (error) {
      console.error('Alpha Vantage forex rate error:', error);
      return null;
    }
  },

  /**
   * Get cryptocurrency quote
   */
  async getCryptoQuote(symbol: string, market: string = 'USD') {
    if (!ALPHA_VANTAGE_API_KEY || ALPHA_VANTAGE_API_KEY === 'your-alpha-vantage-api-key-here') {
      console.warn('Alpha Vantage API key not configured');
      return null;
    }

    try {
      const response = await axios.get(ALPHA_VANTAGE_BASE_URL, {
        params: {
          function: 'CURRENCY_EXCHANGE_RATE',
          from_currency: symbol,
          to_currency: market,
          apikey: ALPHA_VANTAGE_API_KEY,
        },
        timeout: 10000,
      });

      return response.data['Realtime Currency Exchange Rate'];
    } catch (error) {
      console.error('Alpha Vantage crypto quote error:', error);
      return null;
    }
  },
};